import { $set, CanvasTexture3D, Color, ConeGeometry3D, Mesh3D, MeshBasicMaterial3D, MeshNormalMaterial3D, Node3D, PerspectiveCamera3D, PlaneGeometry3D, Raycaster3D, Scene3D, StatsComponent, Vector2, Vector3, WebGLRenderer3D } from 'feng3d-three';
import '../../extension/controls/OrbitControls3D';
import { OrbitControls3D } from '../../extension/controls/OrbitControls3D';
import { ImprovedNoise } from '../../extension/thirdparty/three/imports';

let container: HTMLElement;
let stats: StatsComponent;

let camera: PerspectiveCamera3D;
let controls: OrbitControls3D;
let scene: Scene3D;
let renderer: WebGLRenderer3D;

let mesh: Mesh3D;
let texture: CanvasTexture3D;

const worldWidth = 256;
const worldDepth = 256;
const worldHalfWidth = worldWidth / 2;
const worldHalfDepth = worldDepth / 2;

let helper: Mesh3D;

const raycaster = new Raycaster3D();
const pointer = new Vector2();

init();
animate();

function init()
{
    container = document.getElementById('container');
    container.innerHTML = '';

    renderer = new Node3D().addComponent('WebGLRenderer3D', { parameters: { antialias: true } });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(renderer.domElement);

    scene = new Node3D().addComponent('Scene3D');
    scene.background = new Color(0xbfd1e5);

    camera = new Node3D().addComponent('PerspectiveCamera3D', { fov: 60, aspect: window.innerWidth / window.innerHeight, near: 10, far: 20000 });

    controls = new Node3D().addComponent('OrbitControls3D', { camera, domElement: renderer.domElement });
    controls.minDistance = 1000;
    controls.maxDistance = 10000;
    controls.maxPolarAngle = Math.PI / 2;

    //

    const data = generateHeight(worldWidth, worldDepth);

    controls.target.y = data[worldHalfWidth + worldHalfDepth * worldWidth] + 500;
    camera.entity.position.y = controls.target.y + 2000;
    camera.entity.position.x = 2000;
    controls.update();

    const geometry = $set(new PlaneGeometry3D(), { width: 7500, height: 7500, widthSegments: worldWidth - 1, heightSegments: worldDepth - 1 });
    geometry.rotateX(-Math.PI / 2);

    const vertices = geometry.attributes.position.array;

    for (let i = 0, j = 0, l = vertices.length; i < l; i++, j += 3)
    {
        vertices[j + 1] = data[i] * 10;
    }

    //

    texture = new CanvasTexture3D(generateTexture(data, worldWidth, worldDepth));
    texture.wrapS = 'ClampToEdgeWrapping';
    texture.wrapT = 'ClampToEdgeWrapping';

    mesh = new Node3D().addComponent('Mesh3D', {
        geometry, material: $set(new MeshBasicMaterial3D(), { map: texture })
    });
    scene.entity.addChild(mesh.entity);

    const geometryHelper = $set(new ConeGeometry3D(), { radius: 20, height: 100, radialSegments: 3 });
    geometryHelper.translate(0, 50, 0);
    geometryHelper.rotateX(Math.PI / 2);
    helper = new Node3D().addComponent('Mesh3D', {
        geometry: geometryHelper, material: new MeshNormalMaterial3D()
    });
    scene.entity.addChild(helper.entity);

    container.addEventListener('pointermove', onPointerMove);

    stats = scene.addComponent('StatsComponent');
    container.appendChild(stats.dom);

    //

    window.addEventListener('resize', onWindowResize);
}

function onWindowResize()
{
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();

    renderer.setSize(window.innerWidth, window.innerHeight);
}

function generateHeight(width: number, height: number)
{
    const size = width * height;
    const data = new Uint8Array(size);
    const perlin = new ImprovedNoise();
    const z = Math.random() * 100;

    let quality = 1;

    for (let j = 0; j < 4; j++)
    {
        for (let i = 0; i < size; i++)
        {
            const x = i % width; const
                y = ~~(i / width);
            data[i] += Math.abs(perlin.noise(x / quality, y / quality, z) * quality * 1.75);
        }

        quality *= 5;
    }

    return data;
}

function generateTexture(data: Uint8Array, width: number, height: number)
{
    // bake lighting into texture

    let context: CanvasRenderingContext2D;
    let image: ImageData;
    let imageData: Uint8ClampedArray;
    let shade: number;

    const vector3 = new Vector3(0, 0, 0);

    const sun = new Vector3(1, 1, 1);
    sun.normalize();

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    context = canvas.getContext('2d');
    context.fillStyle = '#000';
    context.fillRect(0, 0, width, height);

    image = context.getImageData(0, 0, canvas.width, canvas.height);
    imageData = image.data;

    for (let i = 0, j = 0, l = imageData.length; i < l; i += 4, j++)
    {
        vector3.x = data[j - 2] - data[j + 2];
        vector3.y = 2;
        vector3.z = data[j - width * 2] - data[j + width * 2];
        vector3.normalize();

        shade = vector3.dot(sun);

        imageData[i] = (96 + shade * 128) * (0.5 + data[j] * 0.007);
        imageData[i + 1] = (32 + shade * 96) * (0.5 + data[j] * 0.007);
        imageData[i + 2] = (shade * 96) * (0.5 + data[j] * 0.007);
    }

    context.putImageData(image, 0, 0);

    // Scaled 4x

    const canvasScaled = document.createElement('canvas');
    canvasScaled.width = width * 4;
    canvasScaled.height = height * 4;

    context = canvasScaled.getContext('2d');
    context.scale(4, 4);
    context.drawImage(canvas, 0, 0);

    image = context.getImageData(0, 0, canvasScaled.width, canvasScaled.height);
    imageData = image.data;

    for (let i = 0, l = imageData.length; i < l; i += 4)
    {
        const v = ~~(Math.random() * 5);

        imageData[i] += v;
        imageData[i + 1] += v;
        imageData[i + 2] += v;
    }

    context.putImageData(image, 0, 0);

    return canvasScaled;
}

//

function animate()
{
    requestAnimationFrame(animate);

    render();
    stats.update();
}

function render()
{
    renderer.render(scene, camera);
}

function onPointerMove(event: PointerEvent)
{
    pointer.x = (event.clientX / renderer.domElement.clientWidth) * 2 - 1;
    pointer.y = -(event.clientY / renderer.domElement.clientHeight) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);

    // See if the ray from the camera into the world hits one of our meshes
    const intersects = raycaster.intersectObject(mesh.entity);

    // Toggle rotation bool for meshes that we clicked
    if (intersects.length > 0)
    {
        helper.entity.position.set(0, 0, 0);
        helper.entity.lookAt(intersects[0].face.normal);

        helper.entity.position.copy(intersects[0].point);
    }
}
